// External Imports
import { Fragment, useContext, useEffect, useState } from "react";
import Heading from "../UI/Heading";
import VehicleFlags from "../vehicles-page/VehicleFlags";
import { UtilityContext } from "../../context/util-context";
import { useSearchParams } from "react-router-dom";
import { requestAdminAPI } from "../../api/posh-api";
import Notes from "../UI/Notes";
import LinkPair from "../UI/LinkPair";
import { DataContext } from "../../context/data-context";
import SelectPair from "../UI/SelectPair";
import DetailPair from "../UI/DetailPair";
import ImageSelector from "../UI/ImageSelector";
import { isoDateToString } from "../../util/format-date";
import { MdAdd, MdOutlineSendAndArchive, MdRefresh } from "react-icons/md";
import ExpenseCard from "../expenses-page/ExpenseCard";
import NoRecordsFound from "../UI/NoRecordsFound";
import AddExpense from "../expenses-page/AddExpense";
import { TbFileTypePdf } from "react-icons/tb";
import LoadingSpinner from "../UI/LoadingSpinner";
import AuditLogs from "../UI/AuditLogs";

// Internal Imports

/** ========================================================================
 * * MaintenanceInfo Component
 * This component renders a card that shows details of a maintenance log.
 * ========================================================================== */

export default function MaintenanceInfo() {
  const { navigate, setConfirm } = useContext(UtilityContext);
  const { admin } = useContext(DataContext);

  // Get Booking ID from URL
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  // Initialize States
  const [log, setLog] = useState({});
  const [addExpense, setAddExpense] = useState(false);
  const [loading, setLoading] = useState(true);

  /* ======================= FETCH RECORD ======================= */
  const fetchRecord = async () => {
    try {
      setLoading(true);
      const res = await requestAdminAPI("fetch", {
        table: "maintenance",
        id,
        columns: [
          "vehicle {id, year, model, vin, cornerImage, make { name image }, license, licenseState, trackerId, status }",
          "reportedBy { name }",
          "expenses: expenses_on_maintenance { id status dueDate datePaid category createdBy { name } description amount }",
        ],
      });
      if (res.applications?.length > 0) {
        res.application = res.applications[0];
        delete res.applications;
      }
      setLog(res);
      setLoading(false);
      // If in progress get current mileage
      if (res.status === "in progress") {
        const report = await requestAdminAPI("fetch-vehicle-reports", {
          vehicles: [{ vin: res.vehicle.id, trackerId: res.vehicle.trackerId }],
        });
        // Update the form fields with the vehicle's report data
        if (report[0])
          document.getElementById("endMileage").value =
            report[0].report.mileage;
      }
    } catch (err) {
      console.error(err);

      setLoading(false);
    }
  };

  /* ======================= UPDATE STATUS ======================= */
  const udpateStatus = async (e) => {
    e.preventDefault();
    try {
      const by = e.target.by.value;
      const diagnosis = e.target.diagnosis?.value;
      const endMileage = e.target.endMileage?.value;
      const available = e.target["Vehicle Availability"]?.value;
      setLoading(true);
      const res = await requestAdminAPI("update-maintenance", {
        table: "maintenance",
        id,
        by: by,
        diagnosis: diagnosis,
        endMileage: endMileage,
        available: available,
      });
      fetchRecord();
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= UPDATE STATUS MESSAGE ======================= */
  const updateStatusMessage = () => {
    let message = "";
    switch (log.status?.toLowerCase()) {
      case "reported":
        message = "Who performed the diagnosis";
        break;
      case "diagnosed":
        message = "Who will be performing the maintenance";
        break;
      case "in progress":
        message = "Who verfied that the maintenance was completed";
        break;
      default:
        return "";
    }
    return message;
  };

  /* ======================= GET PDF ======================= */
  const getPDF = async (email) => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("print-job-order", {
        maintenanceId: log.id,
        email: email || false,
      });
      window.open(response, "_blank");
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Fetch Record on Load
  useEffect(() => {
    fetchRecord();
  }, []);

  return (
    <div className="container p-6">
      {addExpense && (
        <AddExpense
          open={addExpense}
          setOpen={setAddExpense}
          defaultCategory="vehicle-maintenance"
          defaultSelected={log?.vehicle?.id}
          onComplete={fetchRecord}
          association={{ maintenanceId: log.id }}
        />
      )}
      <Heading
        title="Maintenance Log"
        isBackArrow={true}
        text="Changing maintenance logs sends notifications. Status can only be updated step by step. Vehicle mileage is automatically fetched from the tracker."
        id={id}
      >
        {loading ? (
          <LoadingSpinner classes="w-6 h-6 text-white" />
        ) : (
          <Fragment>
            <MdOutlineSendAndArchive
              className="icon"
              onClick={() =>
                setConfirm({
                  message:
                    "This will send an email including the maintenance job order PDF to admin@dallascollisioncare.com.",
                  action: () => getPDF(true),
                })
              }
            />
            <TbFileTypePdf className="icon" onClick={() => getPDF(false)} />
            <AuditLogs table="maintenance" id={id} />
            <MdRefresh className="icon" onClick={fetchRecord} />
          </Fragment>
        )}
      </Heading>

      <div className="grid items-start gap-8 text-sm">
        <div className="grid gap-4 lg:grid-cols-3">
          <div
            className="grid gap-4"
            onClick={() => navigate(`/vehicles/info?id=${log.vehicle?.id}`)}
          >
            <div className="flex items-center space-x-4">
              <img
                src={log.vehicle?.make?.image}
                className="object-contain w-20"
              />
              <div>
                <p className="text-2xl font-bold">
                  {log?.vehicle?.year} {log?.vehicle?.make?.name}{" "}
                  {log?.vehicle?.model}
                </p>
                <p className="text-base text-stone-400">{log?.vehicle?.vin}</p>
              </div>
            </div>
            <ImageSelector
              loading={loading}
              image={log.vehicle?.cornerImage}
              classes="h-48 lg:w-3/4"
              name="cornerImage"
              label="CORNER IMAGE"
            />
          </div>

          <div className="info lg:col-span-2">
            <DetailPair
              label="Status"
              classes={
                log.status === "completed"
                  ? "text-green-600 font-bold text-lg"
                  : log.status === "in progress"
                    ? "text-blue-600 font-bold text-lg"
                    : log.status === "diagnosed"
                      ? "text-yellow-600 font-bold text-lg"
                      : "text-red-600 font-bold text-lg"
              }
              value={log.status?.toUpperCase()}
              loading={loading}
            />
            <DetailPair
              label="Start Mileage"
              value={log.startMileage}
              loading={loading}
            />
            <DetailPair
              label="End Mileage"
              value={log.endMileage}
              loading={loading}
            />
            <LinkPair link={log.image} label="Image of Issue" />
            <DetailPair
              label="Date Reported"
              value={isoDateToString(log.dateCreated)}
              loading={loading}
            />
            <DetailPair
              label="Reported By"
              value={log.reportedBy?.name}
              loading={loading}
            />
            <div className="lg:col-span-2">
              <DetailPair
                label="Issue"
                value={log.description}
                classes="font-semibold"
                loading={loading}
              />
            </div>
            <DetailPair
              label="Date Diagnosed"
              value={isoDateToString(log.dateDiagnosed)}
              loading={loading}
            />
            <DetailPair
              label="Diagnosed By"
              value={log.diagnosedBy}
              loading={loading}
            />
            <div className="lg:col-span-2">
              <DetailPair
                label="Diagnosis"
                value={log.diagnosis}
                classes="font-semibold"
                loading={loading}
              />
            </div>
            <DetailPair
              label="Date Started"
              value={isoDateToString(log.dateProgress)}
              loading={loading}
            />
            <DetailPair
              label="Handled By"
              value={log.progressBy}
              loading={loading}
            />
            <div className="lg:col-span-2"></div>
            <DetailPair
              label="Date Completed"
              value={isoDateToString(log.dateCompleted)}
              loading={loading}
            />
            <DetailPair
              label="Verified By"
              value={log.completedBy}
              loading={loading}
            />
            <DetailPair
              label="Total Cost"
              value={`$${log.expenses?.reduce(
                (acc, expense) => acc + (expense.amount || 0),
                0,
              )}`}
              classes="font-semibold text-lg"
              loading={loading}
            />
          </div>
        </div>

        {log.status !== "completed" && (
          <form
            onSubmit={udpateStatus}
            className="grid items-end gap-4 lg:grid-cols-4"
          >
            {log.status?.toLowerCase() === "reported" && (
              <div className="lg:col-span-2">
                <DetailPair
                  label="Describe the diagnosis in detail below (what needs to be
                      done etc.)"
                  edit={true}
                  inputOptions={{
                    id: "diagnosis",
                    type: "text",
                    required: true,
                  }}
                  loading={loading}
                />
              </div>
            )}
            {log.status?.toLowerCase() === "in progress" && (
              <div className="grid gap-4 lg:grid-cols-2 lg:col-span-2">
                <DetailPair
                  label="Current Mileage"
                  edit={true}
                  inputOptions={{ id: "endMileage", type: "number" }}
                />
                <SelectPair
                  label="Vehicle Availability"
                  options={[
                    { id: false, name: "Do not change" },
                    { id: true, name: "Mark as available" },
                  ]}
                  loading={loading}
                  edit={true}
                />
              </div>
            )}
            <DetailPair
              label={updateStatusMessage()}
              edit={true}
              loading={loading}
              inputOptions={{
                defaultValue: admin?.name,
                name: "by",
                autoComplete: "off",
                required: true,
              }}
            />
            <button type="submit" className="btn-primary w-fit">
              {log.status === "in progress"
                ? "Mark as Completed"
                : "Update Status"}
            </button>
          </form>
        )}
        <div>
          <Heading
            title="Maintenance Expenses"
            isBackArrow={false}
            onClick={() => setAddExpense(true)}
            text="List of expenses associated with this maintenance log."
          >
            <MdAdd
              className="icon"
              title="Add Expense"
              onClick={() => setAddExpense(true)}
            />
          </Heading>
          <div className="list">
            {log?.expenses?.length > 0 ? (
              log.expenses?.map((expense, idx) => (
                <ExpenseCard
                  expense={{ ...expense, association: expense.createdBy?.name }}
                  key={idx}
                />
              ))
            ) : (
              <NoRecordsFound />
            )}
          </div>
        </div>
        <Notes table="maintenance" id={log.id} />
      </div>
    </div>
  );
}
